// UIContext.js

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import {
  getStoredShellMode,
  isSimpleShellMode,
  setStoredShellMode,
  SHELL_MODE,
} from 'utilities/shellMode';

const UIContext = createContext(null);

const SHELL_MODE_CYCLE = Object.freeze([
  SHELL_MODE.SIMPLE,
  SHELL_MODE.CONNECT,
  SHELL_MODE.WORKSHOP,
]);

export const UIProvider = ({ children }) => {
  const [background, setBackground] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [newUserProfileSetup, setNewUserProfileSetup] = useState(false);
  const [shellMode, setShellModeState] = useState(() => getStoredShellMode());
  // User preference for the desktop icon rail (manual collapse / expand).
  const [sidebarRailUserCompact, setSidebarRailCompact] = useState(false);
  // Viewport ≤1023px — always keeps the rail compact (iPad / small desktop).
  const [sidebarRailNarrowViewport, setSidebarRailNarrowViewport] = useState(false);

  const isSidebarRailCompact = sidebarRailUserCompact || sidebarRailNarrowViewport;

  const setShellMode = useCallback((mode) => {
    setShellModeState(setStoredShellMode(mode));
  }, []);

  const toggleShellMode = useCallback(() => {
    setShellModeState((prev) => {
      const index = SHELL_MODE_CYCLE.indexOf(prev);
      const next = SHELL_MODE_CYCLE[(index + 1) % SHELL_MODE_CYCLE.length]
        || SHELL_MODE.SIMPLE;
      return setStoredShellMode(next);
    });
  }, []);

  const expandSidebarRail = useCallback(() => {
    // Narrow viewports stay compact; expand only clears the sticky user preference.
    setSidebarRailCompact(false);
  }, []);

  const collapseSidebarRail = useCallback(() => {
    setSidebarRailCompact(true);
  }, []);

  const value = useMemo(() => ({
    background,
    setBackground,
    selectedLocation,
    setSelectedLocation,
    loginOpen,
    setLoginOpen,
    newUserProfileSetup,
    setNewUserProfileSetup,
    shellMode,
    isSimpleShell: isSimpleShellMode(shellMode),
    isConnectShell: shellMode === SHELL_MODE.CONNECT,
    isAppsShell: shellMode === SHELL_MODE.SIMPLE,
    setShellMode,
    toggleShellMode,
    isSidebarRailCompact,
    sidebarRailNarrowViewport,
    setSidebarRailCompact,
    setSidebarRailNarrowViewport,
    expandSidebarRail,
    collapseSidebarRail,
  }), [
    background,
    selectedLocation,
    loginOpen,
    newUserProfileSetup,
    shellMode,
    setShellMode,
    toggleShellMode,
    isSidebarRailCompact,
    sidebarRailNarrowViewport,
    expandSidebarRail,
    collapseSidebarRail,
  ]);

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within UIProvider');
  }
  return context;
};

export default UIContext;
